import React, { useState, useRef, useEffect } from 'react';
import { Upload, File, CheckCircle, XCircle, AlertCircle, Eye, Download, Edit, Save, RotateCcw, History, Plus, Trash2, AlertTriangle, FileText, Settings } from 'lucide-react';
import Editor from '@monaco-editor/react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { schemas, JSONValidator, exampleGenerators } from '../utils/jsonSchemas';
import NewsEditor from '../components/forms/NewsEditor';
import ResourceEditor from '../components/forms/ResourceEditor';

const AdminPanel = () => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('files');
  const [backups, setBackups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [validationResults, setValidationResults] = useState({ valid: true, errors: [], warnings: [] });
  const [showFormEditor, setShowFormEditor] = useState(false);
  const [formEditorType, setFormEditorType] = useState('news');
  const [editingItem, setEditingItem] = useState(null);
  const [currentFileData, setCurrentFileData] = useState([]);
  const fileInputRef = useRef(null);

  // Password simple (en producción usar algo más seguro)
  const ADMIN_PASSWORD = 'grana2025admin';

  const handleAuth = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      loadExistingFiles();
      loadBackups();
    } else {
      alert('Contraseña incorrecta');
    }
  };

  const loadBackups = async () => {
    try {
      const response = await fetch('/api/json-manager.php?action=list-backups');
      if (response.ok) {
        const data = await response.json();
        setBackups(data.backups || []);
      }
    } catch (error) {
      console.warn('No se pudieron cargar los backups:', error);
    }
  };

  const startEditing = (file) => {
    setEditingFile(file);
    setEditContent(file.content || '{}');
    setActiveTab('editor');
    validateContent(file.content || '{}', file.name);
  };

  const validateContent = (content, filename) => {
    try {
      const data = JSON.parse(content);
      const validation = JSONValidator.validateFile(filename, data);
      setValidationResults(validation);
    } catch (error) {
      setValidationResults({
        valid: false,
        errors: [{ path: 'root', message: 'JSON inválido: ' + error.message, severity: 'error' }],
        warnings: []
      });
    }
  };

  const handleEditorChange = (value) => {
    setEditContent(value || '');
    if (editingFile) {
      validateContent(value || '', editingFile.name);
    }
  };

  const addNewItem = () => {
    if (!editingFile || !exampleGenerators[editingFile.name]) return;
    
    try {
      const currentData = JSON.parse(editContent);
      if (Array.isArray(currentData)) {
        const newItem = exampleGenerators[editingFile.name]();
        const newData = [...currentData, newItem];
        const newContent = JSON.stringify(newData, null, 2);
        setEditContent(newContent);
        validateContent(newContent, editingFile.name);
      }
    } catch (error) {
      alert('Error al añadir nuevo elemento: JSON inválido');
    }
  };

  const openFormEditor = async (filename, itemId = null) => {
    try {
      // Cargar datos del archivo
      const response = await fetch(`/data/${filename}?v=${Date.now()}`);
      const data = await response.json();
      
      setCurrentFileData(data);
      
      if (filename === 'news.json') {
        setFormEditorType('news');
        if (itemId) {
          const item = data.find(item => item.id === itemId);
          setEditingItem(item);
        } else {
          setEditingItem(null); // Importante: resetear editingItem para nuevos elementos
        }
      } else if (filename === 'resources.json') {
        setFormEditorType('resources');
        if (itemId) {
          const item = data.find(item => item.id === itemId);
          setEditingItem(item);
        } else {
          setEditingItem(null); // Importante: resetear editingItem para nuevos elementos
        }
      }
      
      setShowFormEditor(true);
      setActiveTab('forms');
    } catch (error) {
      console.error('Error loading file for form editor:', error);
      alert('Error al cargar el archivo para edición');
    }
  };

  const saveFormItem = async (itemData) => {
    try {
      let newData;
      
      if (editingItem && Object.keys(editingItem).length > 0) {
        // Editar elemento existente
        newData = currentFileData.map(item => 
          item.id === editingItem.id ? itemData : item
        );
      } else {
        // Añadir nuevo elemento
        // Si currentFileData está vacío, necesitamos cargar los datos actuales
        let existingData = currentFileData;
        if (existingData.length === 0) {
          try {
            const filename = formEditorType === 'news' ? 'news.json' : 'resources.json';
            const response = await fetch(`/data/${filename}?v=${Date.now()}`);
            if (response.ok) {
              existingData = await response.json();
            }
          } catch (error) {
            console.log('No se pudo cargar el archivo existente, creando nuevo array');
            existingData = [];
          }
        }
        newData = [...existingData, itemData];
      }

      const filename = formEditorType === 'news' ? 'news.json' : 'resources.json';
      const formattedContent = JSON.stringify(newData, null, 2);
      
      // Guardar archivo
      const formData = new FormData();
      const blob = new Blob([formattedContent], { type: 'application/json' });
      
      // Crear el archivo de forma más robusta
      try {
        const file = new File([blob], filename, { type: 'application/json' });
        formData.append('jsonFile', file);
      } catch (fileError) {
        // Fallback para navegadores que no soportan File constructor
        console.warn('File constructor no disponible, usando fallback:', fileError);
        formData.append('jsonFile', blob, filename);
      }

      const response = await fetch('/api/upload-json.php', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();
      
      if (response.ok) {
        alert(`✅ ${editingItem && Object.keys(editingItem).length > 0 ? 'Elemento actualizado' : 'Elemento creado'} correctamente`);
        setShowFormEditor(false);
        setEditingItem(null);
        setActiveTab('files');
        loadExistingFiles();
        loadBackups();
      } else {
        throw new Error(result.error || 'Error al guardar');
      }
    } catch (error) {
      alert(`❌ Error al guardar: ${error.message}`);
    }
  };

  const deleteFormItem = async (itemId) => {
    if (!window.confirm('¿Estás seguro de eliminar este elemento?')) return;
    
    try {
      const newData = currentFileData.filter(item => item.id !== itemId);
      const filename = formEditorType === 'news' ? 'news.json' : 'resources.json';
      const formattedContent = JSON.stringify(newData, null, 2);
      
      // Guardar archivo
      const formData = new FormData();
      const blob = new Blob([formattedContent], { type: 'application/json' });
      
      // Crear el archivo de forma más robusta
      try {
        const file = new File([blob], filename, { type: 'application/json' });
        formData.append('jsonFile', file);
      } catch (fileError) {
        // Fallback para navegadores que no soportan File constructor
        console.warn('File constructor no disponible, usando fallback:', fileError);
        formData.append('jsonFile', blob, filename);
      }

      const response = await fetch('/api/upload-json.php', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();
      
      if (response.ok) {
        alert('✅ Elemento eliminado correctamente');
        // Actualizar datos locales
        setCurrentFileData(newData);
      } else {
        throw new Error(result.error || 'Error al eliminar');
      }
    } catch (error) {
      alert(`❌ Error al eliminar: ${error.message}`);
    }
  };

  const cancelFormEditor = () => {
    setShowFormEditor(false);
    setEditingItem(null);
    setActiveTab('files');
  };

  const saveEditedFile = async () => {
    if (!editingFile) return;
    
    // Validar antes de guardar
    if (!validationResults.valid) {
      const errorCount = validationResults.errors.length;
      if (!window.confirm(`⚠️ Hay ${errorCount} error(es) de validación. ¿Continuar guardando?`)) {
        return;
      }
    }
    
    setSaving(true);
    try {
      // Validar JSON antes de guardar
      JSON.parse(editContent);
      
      const formData = new FormData();
      const blob = new Blob([editContent], { type: 'application/json' });
      
      // Crear el archivo de forma más robusta
      try {
        const file = new File([blob], editingFile.name, { type: 'application/json' });
        formData.append('jsonFile', file);
      } catch (fileError) {
        // Fallback para navegadores que no soportan File constructor
        console.warn('File constructor no disponible, usando fallback:', fileError);
        formData.append('jsonFile', blob, editingFile.name);
      }

      const response = await fetch('/api/upload-json.php', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();
      
      if (response.ok) {
        alert(`✅ Archivo ${editingFile.name} guardado correctamente`);
        setEditingFile(null);
        setActiveTab('files');
        loadExistingFiles();
        loadBackups();
      } else {
        throw new Error(result.error || 'Error al guardar');
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        alert('❌ Error: El JSON no es válido. Verifica la sintaxis.');
      } else {
        alert(`❌ Error al guardar: ${error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const cancelEditing = () => {
    if (editContent !== (editingFile?.content || '{}')) {
      if (window.confirm('¿Estás seguro de cancelar? Se perderán los cambios no guardados.')) {
        setEditingFile(null);
        setActiveTab('files');
      }
    } else {
      setEditingFile(null);
      setActiveTab('files');
    }
  };

  const restoreBackup = async (backupFile) => {
    if (!window.confirm(`¿Restaurar el backup ${backupFile.name}? Esto sobrescribirá el archivo actual.`)) {
      return;
    }

    try {
      const response = await fetch('/api/json-manager.php?action=restore-backup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ backupFile: backupFile.name })
      });

      const result = await response.json();
      
      if (response.ok) {
        alert(`✅ Backup restaurado correctamente`);
        loadExistingFiles();
        loadBackups();
      } else {
        throw new Error(result.error || 'Error al restaurar backup');
      }
    } catch (error) {
      alert(`❌ Error al restaurar backup: ${error.message}`);
    }
  };

  const loadExistingFiles = async () => {
    try {
      // Intentar primero con la API PHP
      try {
        const apiResponse = await fetch('/api/json-manager.php?action=list');
        if (apiResponse.ok) {
          const apiData = await apiResponse.json();
          if (apiData.files) {
            console.log('✅ Usando API PHP para listar archivos');
            setExistingFiles(apiData.files);
            return;
          }
        }
      } catch (apiError) {
        console.warn('API PHP no disponible, usando fallback:', apiError.message);
      }

      // Fallback: lista de archivos conocidos que realmente usa el sitio
      const knownFiles = [
        'news.json', 'resources.json', 'config.json'
      ];

      const files = await Promise.all(
        knownFiles.map(async (filename) => {
          try {
            const response = await fetch(`/data/${filename}?v=${Date.now()}`);
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }
            
            const text = await response.text();
            
            // Verificar si es HTML (error del servidor)
            if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
              throw new Error('Servidor devolvió HTML en lugar de JSON');
            }
            
            // Intentar parsear como JSON
            JSON.parse(text);
            
            return {
              name: filename,
              size: text.length,
              lastModified: response.headers.get('last-modified') || new Date().toLocaleDateString(),
              status: 'success',
              contentType: response.headers.get('content-type') || 'application/json'
            };
          } catch (error) {
            return { 
              name: filename, 
              status: 'error', 
              error: error.message 
            };
          }
        })
      );

      setExistingFiles(files);
    } catch (error) {
      console.error('Error loading files:', error);
      alert('Error cargando archivos. Verifica la consola.');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const jsonFiles = fileArray.filter(file => 
      file.type === 'application/json' || file.name.endsWith('.json')
    );

    if (jsonFiles.length === 0) {
      alert('Solo se permiten archivos JSON');
      return;
    }

    uploadFiles(jsonFiles);
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    const results = [];

    for (const file of files) {
      try {
        // Validar JSON
        const text = await file.text();
        JSON.parse(text); // Esto lanzará error si no es JSON válido

        const formData = new FormData();
        formData.append('jsonFile', file);

        const response = await fetch('/api/upload-json.php', {
          method: 'POST',
          body: formData
        });

        const result = await response.json();
        
        results.push({
          filename: file.name,
          success: response.ok,
          message: result.message || result.error || 'Procesado',
          size: file.size
        });

      } catch (error) {
        results.push({
          filename: file.name,
          success: false,
          message: error.message || 'Error al procesar archivo',
          size: file.size
        });
      }
    }

    setUploadResults(results);
    setUploading(false);
    
    if (results.some(r => r.success)) {
      loadExistingFiles(); // Recargar lista
      alert(`${results.filter(r => r.success).length} archivo(s) actualizado(s)`);
    }
  };

  const viewFileContent = async (filename) => {
    try {
      // Intentar primero con la API PHP
      try {
        const apiResponse = await fetch(`/api/json-manager.php?action=get&file=${encodeURIComponent(filename)}`);
        if (apiResponse.ok) {
          const apiData = await apiResponse.json();
          if (apiData.content) {
            const formattedContent = JSON.stringify(apiData.content, null, 2);
            setSelectedFile({
              name: filename,
              content: formattedContent,
              size: apiData.size,
              lastModified: apiData.lastModified
            });
            return;
          }
        }
      } catch (apiError) {
        console.warn('API PHP falló, intentando fetch directo:', apiError.message);
      }

      // Fallback: fetch directo
      const response = await fetch(`/data/${filename}?v=${Date.now()}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const text = await response.text();
      
      // Verificar si es HTML (error del servidor)
      if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
        throw new Error('El servidor devolvió HTML en lugar de JSON');
      }
      
      // Parsear como JSON
      const data = JSON.parse(text);
      const formattedContent = JSON.stringify(data, null, 2);
      
      setSelectedFile({
        name: filename,
        content: formattedContent,
        rawContent: text
      });
    } catch (error) {
      console.error('Error loading file content:', error);
      alert(`Error cargando archivo: ${error.message}`);
    }
  };

  const downloadFile = (filename) => {
    const link = document.createElement('a');
    link.href = `/data/${filename}`;
    link.download = filename;
    link.click();
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-16">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
            <h1 className="text-2xl font-bold text-center mb-6">Panel de Administración</h1>
            <form onSubmit={handleAuth}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Contraseña de acceso
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Ingrese la contraseña"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 transition-all"
              >
                Acceder
              </button>
            </form>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto p-6 py-16">
        {/* Header con pestañas */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Panel de Administración JSON</h1>
            <p className="text-gray-600">Gestión completa de archivos de configuración</p>
            
            {/* Navegación por pestañas */}
            <div className="flex space-x-6 mt-6">
              <button
                onClick={() => setActiveTab('files')}
                className={`pb-2 border-b-2 transition-colors ${
                  activeTab === 'files' 
                    ? 'border-orange-500 text-orange-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <File className="w-4 h-4 inline mr-2" />
                Archivos
              </button>

              <button
                onClick={() => setActiveTab('forms')}
                className={`pb-2 border-b-2 transition-colors ${
                  activeTab === 'forms' 
                    ? 'border-orange-500 text-orange-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Formularios
              </button>
              
              {editingFile && (
                <button
                  onClick={() => setActiveTab('editor')}
                  className={`pb-2 border-b-2 transition-colors ${
                    activeTab === 'editor' 
                      ? 'border-orange-500 text-orange-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Edit className="w-4 h-4 inline mr-2" />
                  Editor: {editingFile.name}
                </button>
              )}
              
              <button
                onClick={() => setActiveTab('backups')}
                className={`pb-2 border-b-2 transition-colors ${
                  activeTab === 'backups' 
                    ? 'border-orange-500 text-orange-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <History className="w-4 h-4 inline mr-2" />
                Backups ({backups.length})
              </button>
            </div>
          </div>
        </div>

        {/* Contenido según la pestaña activa */}
        {activeTab === 'files' && (
          <>
            {/* Upload Area */}
            <div className="bg-white rounded-lg shadow-sm mb-6">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold">Subir archivos</h2>
              </div>
              <div className="p-6">
                <div
                  className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".json,application/json"
                    onChange={(e) => handleFiles(e.target.files)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    Arrastra archivos JSON aquí
                  </p>
                  <p className="text-gray-600 mb-4">
                    o haz clic para seleccionar archivos
                  </p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 transition-all"
                    disabled={uploading}
                  >
                    {uploading ? 'Subiendo...' : 'Seleccionar archivos'}
                  </button>
                </div>
              </div>
            </div>

            {/* Upload Results */}
            {uploadResults.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm mb-6">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-semibold">Resultados de subida</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {uploadResults.map((result, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        {result.success ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                        <div className="flex-1">
                          <p className="font-medium">{result.filename}</p>
                          <p className="text-sm text-gray-600">{result.message}</p>
                        </div>
                        <span className="text-sm text-gray-500">
                          {(result.size / 1024).toFixed(1)} KB
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Existing Files */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold">Archivos existentes</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {existingFiles.map((file, index) => (
                    <div key={index} className={`border rounded-lg p-4 ${
                      file.status === 'success' ? 'border-gray-200' : 
                      file.status === 'error' ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'
                    }`}>
                      <div className="flex items-start gap-3">
                        {file.status === 'success' ? (
                          <File className="w-5 h-5 text-orange-600 mt-0.5" />
                        ) : file.status === 'error' ? (
                          <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <h3 className="font-medium">{file.name}</h3>
                          {file.status === 'success' ? (
                            <p className="text-sm text-gray-600">
                              {(file.size / 1024).toFixed(1)} KB
                            </p>
                          ) : (
                            <p className="text-sm text-red-600">
                              {file.error || 'Error desconocido'}
                            </p>
                          )}
                        </div>
                      </div>
                      {file.status === 'success' && (
                        <div className="mt-3 flex gap-2 flex-wrap">
                          <button
                            onClick={() => viewFileContent(file.name)}
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                          >
                            <Eye className="w-4 h-4" />
                            Ver
                          </button>
                          <button
                            onClick={async () => {
                              await viewFileContent(file.name);
                              if (selectedFile) {
                                startEditing(selectedFile);
                              }
                            }}
                            className="flex items-center gap-1 text-sm text-orange-600 hover:text-orange-800"
                          >
                            <Edit className="w-4 h-4" />
                            Editor JSON
                          </button>
                          {(file.name === 'news.json' || file.name === 'resources.json') && (
                            <button
                              onClick={() => openFormEditor(file.name)}
                              className="flex items-center gap-1 text-sm text-green-600 hover:text-green-800"
                            >
                              <FileText className="w-4 h-4" />
                              Formulario
                            </button>
                          )}
                          <button
                            onClick={() => downloadFile(file.name)}
                            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800"
                          >
                            <Download className="w-4 h-4" />
                            Descargar
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Formularios Visuales */}
        {activeTab === 'forms' && (
          <>
            {!showFormEditor ? (
              <div className="bg-white rounded-lg shadow-sm">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-semibold">Formularios Visuales</h2>
                  <p className="text-gray-600">Edición intuitiva de contenido sin JSON</p>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Gestión de Noticias */}
                    <div className="border rounded-lg p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <FileText className="w-8 h-8 text-blue-600" />
                        <div>
                          <h3 className="text-lg font-medium">Noticias</h3>
                          <p className="text-gray-600 text-sm">Gestionar artículos y noticias</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <button
                          onClick={() => {
                            setFormEditorType('news');
                            setCurrentFileData([]);
                            setEditingItem({}); // {} significa nuevo elemento
                            setShowFormEditor(true);
                            setActiveTab('forms');
                          }}
                          className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Nueva Noticia
                        </button>
                        <button
                          onClick={() => openFormEditor('news.json')}
                          className="w-full flex items-center justify-center gap-2 border border-blue-600 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                          Editar Noticias
                        </button>
                      </div>
                    </div>

                    {/* Gestión de Recursos */}
                    <div className="border rounded-lg p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Download className="w-8 h-8 text-green-600" />
                        <div>
                          <h3 className="text-lg font-medium">Recursos</h3>
                          <p className="text-gray-600 text-sm">Gestionar documentos y recursos</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <button
                          onClick={() => {
                            setFormEditorType('resources');
                            setCurrentFileData([]);
                            setEditingItem({}); // {} significa nuevo elemento
                            setShowFormEditor(true);
                            setActiveTab('forms');
                          }}
                          className="w-full flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Nuevo Recurso
                        </button>
                        <button
                          onClick={() => openFormEditor('resources.json')}
                          className="w-full flex items-center justify-center gap-2 border border-green-600 text-green-600 px-4 py-2 rounded-lg hover:bg-green-50 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                          Editar Recursos
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">💡 Ventajas de los formularios visuales:</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Interfaz intuitiva sin necesidad de conocer JSON</li>
                      <li>• Validación automática de campos</li>
                      <li>• Vista previa de imágenes en tiempo real</li>
                      <li>• Gestión fácil de etiquetas y categorías</li>
                      <li>• Edición individual de elementos</li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Lista de elementos cuando se está editando un archivo */}
                {showFormEditor && (editingItem === null || editingItem === undefined) && (
                  <div className="bg-white rounded-lg shadow-sm mb-6">
                    <div className="p-6 border-b border-gray-200">
                      <div className="flex justify-between items-center">
                        <h2 className="text-xl font-semibold">
                          {formEditorType === 'news' ? 'Gestión de Noticias' : 'Gestión de Recursos'}
                        </h2>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingItem({})}
                            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                            Añadir {formEditorType === 'news' ? 'Noticia' : 'Recurso'}
                          </button>
                          <button
                            onClick={cancelFormEditor}
                            className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            Volver
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      {currentFileData.length > 0 ? (
                        <div className="space-y-4">
                          {currentFileData.map((item, index) => (
                            <div key={item.id} className="border rounded-lg p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h3 className="font-medium text-lg">{item.title}</h3>
                                  <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                                    {formEditorType === 'news' ? item.excerpt : item.description}
                                  </p>
                                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                    <span>📅 {item.date}</span>
                                    <span>🏷️ {item.category}</span>
                                    {item.featured && <span className="text-yellow-600">⭐ Destacado</span>}
                                  </div>
                                </div>
                                <div className="flex gap-2 ml-4">
                                  <button
                                    onClick={() => setEditingItem(item)}
                                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                                  >
                                    <Edit className="w-4 h-4" />
                                    Editar
                                  </button>
                                  <button
                                    onClick={() => deleteFormItem(item.id)}
                                    className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Eliminar
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <p className="text-gray-600 text-lg mb-2">
                            No hay {formEditorType === 'news' ? 'noticias' : 'recursos'} disponibles
                          </p>
                          <p className="text-gray-500 text-sm mb-4">
                            Comienza creando {formEditorType === 'news' ? 'tu primera noticia' : 'tu primer recurso'}
                          </p>
                          <button
                            onClick={() => setEditingItem({})}
                            className="flex items-center gap-2 mx-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                            Crear {formEditorType === 'news' ? 'Noticia' : 'Recurso'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Editor de formulario */}
                {showFormEditor && editingItem && typeof editingItem === 'object' && (
                  <>
                    {formEditorType === 'news' && (
                      <NewsEditor
                        news={Object.keys(editingItem).length === 0 ? null : editingItem}
                        onSave={saveFormItem}
                        onCancel={() => setEditingItem(null)}
                      />
                    )}
                    {formEditorType === 'resources' && (
                      <ResourceEditor
                        resource={Object.keys(editingItem).length === 0 ? null : editingItem}
                        onSave={saveFormItem}
                        onCancel={() => setEditingItem(null)}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
        {activeTab === 'editor' && editingFile && (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold">Editor JSON</h2>
                  <p className="text-gray-600">Editando: {editingFile.name}</p>
                  {schemas[editingFile.name] && (
                    <p className="text-sm text-blue-600">
                      📋 {schemas[editingFile.name].name} - {schemas[editingFile.name].description}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {exampleGenerators[editingFile.name] && (
                    <button
                      onClick={addNewItem}
                      className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all"
                    >
                      <Plus className="w-4 h-4" />
                      Añadir elemento
                    </button>
                  )}
                  <button
                    onClick={saveEditedFile}
                    disabled={saving}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all disabled:opacity-50 ${
                      validationResults.valid 
                        ? 'bg-green-600 text-white hover:bg-green-700' 
                        : 'bg-yellow-600 text-white hover:bg-yellow-700'
                    }`}
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Guardando...' : validationResults.valid ? 'Guardar' : 'Guardar con errores'}
                  </button>
                  <button
                    onClick={cancelEditing}
                    className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-all"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Cancelar
                  </button>
                </div>
              </div>
            </div>

            {/* Panel de validación */}
            {(validationResults.errors.length > 0 || validationResults.warnings.length > 0) && (
              <div className="p-6 border-b border-gray-200">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  Validación del esquema
                </h3>
                
                {validationResults.errors.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-red-700 mb-2">
                      ❌ Errores ({validationResults.errors.length})
                    </h4>
                    <div className="space-y-1">
                      {validationResults.errors.map((error, index) => (
                        <div key={index} className="text-sm bg-red-50 text-red-700 p-2 rounded">
                          <span className="font-mono text-xs text-red-600">{error.path}:</span> {error.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {validationResults.warnings.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-yellow-700 mb-2">
                      ⚠️ Advertencias ({validationResults.warnings.length})
                    </h4>
                    <div className="space-y-1">
                      {validationResults.warnings.map((warning, index) => (
                        <div key={index} className="text-sm bg-yellow-50 text-yellow-700 p-2 rounded">
                          <span className="font-mono text-xs text-yellow-600">{warning.path}:</span> {warning.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="p-6">
              <div className="border rounded-lg overflow-hidden">
                <Editor
                  height="500px"
                  defaultLanguage="json"
                  value={editContent}
                  onChange={handleEditorChange}
                  theme="vs-dark"
                  options={{
                    formatOnPaste: true,
                    formatOnType: true,
                    automaticLayout: true,
                    scrollBeyondLastLine: false,
                    minimap: { enabled: false },
                    lineNumbers: 'on',
                    roundedSelection: false,
                    scrollbar: { vertical: 'visible' },
                    wordWrap: 'on'
                  }}
                />
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">💡 Consejos para editar JSON:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Usa Ctrl+S (Cmd+S en Mac) para guardar rápidamente</li>
                  <li>• El editor validará automáticamente la sintaxis y esquema JSON</li>
                  <li>• Se crea un backup automático antes de guardar cambios</li>
                  <li>• Usa Ctrl+Z para deshacer cambios</li>
                  {exampleGenerators[editingFile.name] && (
                    <li>• Usa el botón "Añadir elemento" para crear nuevos elementos con la estructura correcta</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Gestión de Backups */}
        {activeTab === 'backups' && (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold">Gestión de Backups</h2>
              <p className="text-gray-600">Backups automáticos de archivos JSON</p>
            </div>
            <div className="p-6">
              {backups.length === 0 ? (
                <div className="text-center py-8">
                  <History className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-600">No hay backups disponibles</p>
                  <p className="text-sm text-gray-500 mt-2">Los backups se crean automáticamente al editar archivos</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {backups.map((backup, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium">{backup.originalFile}</h3>
                          <p className="text-sm text-gray-600">
                            Backup: {backup.backupFile}
                          </p>
                          <p className="text-sm text-gray-500">
                            Creado: {backup.date} | Tamaño: {(backup.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => downloadFile(`backups/${backup.backupFile}`)}
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                          >
                            <Download className="w-4 h-4" />
                            Descargar
                          </button>
                          <button
                            onClick={() => restoreBackup(backup)}
                            className="flex items-center gap-1 text-sm text-green-600 hover:text-green-800"
                          >
                            <RotateCcw className="w-4 h-4" />
                            Restaurar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* File Viewer Modal */}
        {selectedFile && activeTab === 'files' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold">{selectedFile.name}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => startEditing(selectedFile)}
                    className="flex items-center gap-1 text-sm bg-orange-600 text-white px-3 py-1 rounded hover:bg-orange-700"
                  >
                    <Edit className="w-4 h-4" />
                    Editar
                  </button>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>
              <div className="p-4 overflow-auto max-h-[60vh]">
                <Editor
                  height="400px"
                  defaultLanguage="json"
                  value={selectedFile.content}
                  theme="vs-light"
                  options={{
                    readOnly: true,
                    automaticLayout: true,
                    scrollBeyondLastLine: false,
                    minimap: { enabled: false },
                    lineNumbers: 'on'
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AdminPanel;